/**
 * Platform-agnostic SkySlope checklist grouping shared across the compliance
 * surfaces (the agent compliance review page, the extension/in-app document
 * list, and the embedded SkySlope drawer).
 *
 * The grouping rule is the single source of truth for "which documents belong
 * to the same checklist item": documents sharing a `skyslopeActivityId` with a
 * non-blank `skyslopeActivityName` form one checklist item; every other
 * document becomes its own standalone item keyed by its identity.
 *
 * Callers pass a `getKeys` accessor so each surface can use its own document
 * shape (the extension fragment keys on `id`; the review page keys on
 * `ingestionDocumentId`) without the grouper depending on a field name. They
 * then layer their own per-surface state (needs-review, resubmission,
 * aggregated validation status) on top of these pure groups.
 */

/** The identity + grouping keys the grouper reads from each document. */
export interface ChecklistGroupKeys {
  /** Stable document identity — the standalone group key + selection key. */
  id: string;
  /** Display title used when the document isn't on a checklist. */
  documentName: string;
  /** SkySlope checklist activity id (the group key when present). */
  skyslopeActivityId?: string | null;
  /** SkySlope checklist activity display name (the group title when present). */
  skyslopeActivityName?: string | null;
}

/** One checklist item: a checklist activity plus the documents under it. */
export interface ChecklistGroup<TDoc> {
  /** Group key: the `skyslopeActivityId`, or the lone document id when ungrouped. */
  id: string;
  /** Display title: the checklist activity name, or the document name when ungrouped. */
  title: string;
  /**
   * True when this item maps to a real SkySlope checklist activity; false for a
   * standalone document not attached to any checklist requirement.
   */
  isChecklistItem: boolean;
  /** The documents under this item, in incoming order. */
  docs: TDoc[];
}

/**
 * Standard {@link ChecklistGroupKeys} accessor for the Indigo ingestion-document
 * fragment shape (keyed on `id`). Shared by every compliance surface that groups
 * `ExternalIngestionDocumentFragment` / `SharedIngestionDocumentFragment` docs —
 * the document list, the folder summary banner, the in-app drawer, and the
 * extension folder page — so the mapping lives in exactly one place. (The agent
 * review page keys on `ingestionDocumentId` instead and passes its own accessor.)
 */
export const getChecklistKeysFromIngestionDocument = <
  TDoc extends {
    id: string;
    documentName: string;
    skyslopeActivityId?: string | null;
    skyslopeActivityName?: string | null;
  },
>(
  document: TDoc
): ChecklistGroupKeys => ({
  id: document.id,
  documentName: document.documentName,
  skyslopeActivityId: document.skyslopeActivityId,
  skyslopeActivityName: document.skyslopeActivityName,
});

/**
 * Groups documents into SkySlope checklist items.
 *
 * A document belongs to a real checklist activity only when it has BOTH an
 * activity id AND a non-blank activity name (SkySlope can return `''`); such a
 * document is treated as not-on-checklist and becomes its own item titled by
 * its document name.
 *
 * Order follows the incoming order: each item is positioned at its first
 * member, and documents stay in their incoming order within an item.
 */
export const groupDocsIntoChecklistItems = <TDoc>(
  docs: TDoc[],
  getKeys: (doc: TDoc) => ChecklistGroupKeys
): ChecklistGroup<TDoc>[] => {
  const itemById = new Map<string, ChecklistGroup<TDoc>>();
  const items: ChecklistGroup<TDoc>[] = [];

  for (const doc of docs) {
    const keys = getKeys(doc);
    const activityName = keys.skyslopeActivityName?.trim();
    const hasActivity =
      keys.skyslopeActivityId != null &&
      activityName != null &&
      activityName !== '';

    const id =
      hasActivity && keys.skyslopeActivityId != null
        ? keys.skyslopeActivityId
        : keys.id;

    let item = itemById.get(id);
    if (!item) {
      item = {
        id,
        title:
          hasActivity && activityName != null
            ? activityName
            : keys.documentName,
        isChecklistItem: hasActivity,
        docs: [],
      };
      itemById.set(id, item);
      items.push(item);
    }

    item.docs.push(doc);
  }

  return items;
};
